import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  URL_SETTING_OVERRIDE_CLEAR_PARAM,
  URL_SETTING_OVERRIDE_PREFIX,
  UrlSettingOverridesService,
} from '@luggagehero/shared/app-settings/data-access';
import { WindowPlatformService } from '@luggagehero/shared/services/window';

/** One overridden setting, flattened for display. */
interface OverriddenSetting {
  name: string;
  value: string;
}

/**
 * Shows which app settings are being overridden from the URL for this session (AM-4658).
 *
 * Deliberately hard to miss and deliberately not branded. The mechanism's real risk is social rather
 * than technical — someone screenshots an app that was quietly made to look different, or reports a bug
 * nobody else can reproduce — and this badge is the mitigation for both: whatever else is on screen, the
 * session is visibly not the default one. That is also why it is neutral dark chrome rather than the
 * brand palette: the design system reserves orange for scarcity, and a diagnostic overlay must read as
 * *not part of the product*.
 *
 * Renders nothing when no override is in force, so it is inert in a normal session — including in
 * production, where the mechanism is off by default.
 */
@Component({
  selector: 'lh-app-settings-override-indicator',
  imports: [],
  templateUrl: './app-settings-override-indicator.component.html',
  styleUrl: './app-settings-override-indicator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppSettingsOverrideIndicatorComponent {
  private readonly urlOverrides = inject(UrlSettingOverridesService);
  private readonly window = inject(WindowPlatformService);

  /** The query param that clears every override, surfaced so the badge can name it. */
  public readonly clearParam = URL_SETTING_OVERRIDE_CLEAR_PARAM;

  /** Whether the detail list is expanded. Collapsed by default so the badge stays out of the way. */
  public readonly isExpanded = signal(false);

  private readonly active = toSignal(this.urlOverrides.active$, { initialValue: {} });

  /** The overrides in force, as display rows. */
  public readonly overrides = computed<OverriddenSetting[]>(() => {
    const active = this.active();
    return Object.keys(active)
      .sort()
      .map((name) => ({ name, value: this.formatValue(active[name]) }));
  });

  /** Whether to render at all — nothing shows in a session with no overrides. */
  public readonly isVisible = computed(() => this.overrides().length > 0);

  public toggle(): void {
    this.isExpanded.update((expanded) => !expanded);
  }

  /**
   * Drops the overrides and reloads.
   *
   * The reload is the point, not a convenience: clearing storage alone leaves the already-assembled
   * settings object overridden for the rest of the page's life, so the badge would disappear while the
   * app still behaved as though it were overridden — the exact confusion this component exists to
   * prevent. A reload is what makes "cleared" mean cleared.
   *
   * It reloads onto a URL with the `lh_ft_*` params **stripped**, which is the whole trick: a plain
   * `reload()` replays the current query string, so the app initializer's `capture(params)` re-stores
   * the very overrides just cleared and `apply()` re-activates them. The button would then appear to do
   * nothing — precisely on the page the link was opened on, and only until an in-app navigation drops
   * the params, making it intermittent rather than obviously broken.
   *
   * `location` is probed rather than used outright only as a cheap guard: `WindowPlatformService` has no
   * `@Injectable`, and no app provides it for `ionic-traveler`, so its resolved shape there is not
   * obvious from the wiring. Verified empirically instead — serving `ionic-traveler` and loading it with
   * an `lh_ft_` param captures the override, renders this badge, and clears correctly, so `location` is
   * real (`href`/`replace`/`reload` all present) and this path behaves the same as on web.
   */
  public clear(): void {
    this.urlOverrides.clear();

    const location = this.window.location;
    const cleanedHref = this.hrefWithoutOverrideParams(location?.href);

    if (cleanedHref && typeof location.replace === 'function') {
      location.replace(cleanedHref);
    } else if (typeof location?.reload === 'function') {
      location.reload();
    }
  }

  /** The current URL with every `lh_ft_*` param removed, or null if it can't be parsed. */
  private hrefWithoutOverrideParams(href: string | undefined): string | null {
    if (!href) {
      return null;
    }

    try {
      const url = new URL(href);

      // `forEach` rather than spreading `keys()`: the iterator overloads live in `lib.dom.iterable`,
      // which the lib tsconfigs here don't include (only `dom`), so the spread degrades to `any` and
      // trips typed linting. Collect first, then delete — mutating mid-iteration is unsafe.
      const overrideParams: string[] = [];
      url.searchParams.forEach((_value, key) => {
        if (key.startsWith(URL_SETTING_OVERRIDE_PREFIX)) {
          overrideParams.push(key);
        }
      });

      for (const param of overrideParams) {
        url.searchParams.delete(param);
      }

      return url.toString();
    } catch {
      return null;
    }
  }

  private formatValue(value: unknown): string {
    if (Array.isArray(value)) {
      return value.length === 0 ? '(empty)' : (value as unknown[]).map((entry) => this.formatValue(entry)).join(', ');
    }
    // Coercion only ever produces primitives or string arrays, but the signal is typed `unknown`, so
    // handle the rest explicitly rather than letting an object stringify to '[object Object]'.
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (value === null || value === undefined) {
      return '(unset)';
    }
    return JSON.stringify(value);
  }
}
